import { t } from 'i18next';
import { usePostHog } from 'posthog-js/react';
import { useCallback, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { i18n } from '@/lib/i18n';
import { useImage2VideoResult } from '../api/aiImageToVideo';
import { useGenerateVideo } from '../api/generateVideo';
import { useAdjustments, useCanGenerate, useRuntime, useStyle, useUi } from '../atoms/selectors';
import type { ActiveTab, CropAspectOption, ImageRef } from '../atoms/state';
import type { AspectRatio, Mode, Quality } from '../constants/options';
import { MODE_RULES } from '../constants/options';

export const useImageToVideoActions = () => {
  const posthog = usePostHog();

  const [style, setStyle] = useStyle();
  const [runtime, setRuntime] = useRuntime();
  const [ui, setUi] = useUi();
  const [adjustments, setAdjustments] = useAdjustments();

  const canGenerate = useCanGenerate();
  const generateVideoMutation = useGenerateVideo();

  const objectUrlRef = useRef<string | null>(null);
  const lastSavedTaskIdRef = useRef<string | null>(null);

  const { data: result, error: resultError } = useImage2VideoResult({
    taskId: runtime.taskId ?? null,
  });

  // Handle result polling -> blob URL + runtime state
  useEffect(() => {
    if (!result || result.kind !== 'done') return;

    const currentTaskId = runtime.taskId ?? null;

    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
    }

    const url = URL.createObjectURL(result.blob);
    objectUrlRef.current = url;

    setRuntime((prev) => ({
      ...prev,
      status: 'ready',
      previewUrl: url,
      errorMessage: undefined,
      taskId: undefined,
      lastCompletedTaskId: currentTaskId ?? prev.lastCompletedTaskId,
    }));
    // Persist preview for estate media card (localStorage), once per task
    if (currentTaskId && lastSavedTaskIdRef.current !== currentTaskId && style.estateId) {
      lastSavedTaskIdRef.current = currentTaskId;
      void import('@/features/estate-media/components/media-cards/videos-card').then(
        ({ saveVideoForEstate }) => {
          saveVideoForEstate(style.estateId, {
            previewUrl: url,
            taskId: currentTaskId,
            createdAt: new Date().toISOString(),
          });
        }
      );
    }
  }, [result, runtime.taskId, setRuntime, style.estateId]);

  // Cleanup preview object URL on unmount to avoid memory leaks
  useEffect(
    () => () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    },
    []
  );

  // Surface polling errors into runtime
  useEffect(() => {
    if (!resultError) return;

    setRuntime((prev) => ({
      ...prev,
      status: 'error',
      taskId: undefined,
      errorMessage: undefined,
    }));
  }, [resultError, setRuntime]);

  // ---------- UI actions ----------
  const setTab = useCallback(
    (tab: ActiveTab) => {
      setUi((prev) => ({ ...prev, activeTab: tab }));
    },
    [setUi]
  );

  // ---------- Style actions ----------
  const onEstateChange = useCallback(
    (estateId: string | null) => {
      setStyle((prev) => ({
        ...prev,
        estateId,

        projectId: estateId ? null : prev.projectId,
      }));
    },
    [setStyle]
  );

  const onProjectChange = useCallback(
    (projectId: string | null) => {
      setStyle((prev) => ({
        ...prev,
        projectId,

        estateId: projectId ? null : prev.estateId,
      }));
    },
    [setStyle]
  );

  const onModeChange = useCallback(
    (mode: Mode) => {
      setStyle((prev) => {
        const rules = MODE_RULES[mode];

        const aspectRatio = rules.allowedRatios.includes(prev.aspectRatio)
          ? prev.aspectRatio
          : rules.allowedRatios[0];

        let cropToAspectRatio: CropAspectOption = prev.cropToAspectRatio ?? 'source';
        if (cropToAspectRatio !== 'source' && !rules.allowedRatios.includes(cropToAspectRatio)) {
          cropToAspectRatio = 'source';
        }

        const durationSec = Math.min(
          rules.maxDuration,
          Math.max(rules.minDuration, prev.durationSec)
        );

        const images = prev.images.slice(0, rules.maxImages);
        const startImageId = images[0]?.id;
        const endImageId = images[images.length - 1]?.id;

        return {
          ...prev,
          mode,
          aspectRatio,
          cropToAspectRatio,
          durationSec,
          images,
          startImageId,
          endImageId,
        };
      });
    },
    [setStyle]
  );

  const onAspectRatioChange = useCallback(
    (ratio: AspectRatio) => {
      setStyle((prev) => ({ ...prev, aspectRatio: ratio }));
    },
    [setStyle]
  );

  const onCropToAspectRatioChange = useCallback(
    (value: CropAspectOption) => {
      setStyle((prev) => ({ ...prev, cropToAspectRatio: value }));
    },
    [setStyle]
  );

  const onQualityChange = useCallback(
    (quality: Quality) => {
      setStyle((prev) => ({ ...prev, quality }));
    },
    [setStyle]
  );

  const onDurationChange = useCallback(
    (durationSec: number) => {
      setStyle((prev) => {
        const rules = MODE_RULES[prev.mode];
        const rounded = Math.round(durationSec);
        const clamped = Math.min(rules.maxDuration, Math.max(rules.minDuration, rounded));
        return { ...prev, durationSec: clamped };
      });
    },
    [setStyle]
  );

  // ---------- Images actions ----------
  const onAddImages = useCallback(
    (images: ImageRef[]) => {
      setStyle((prev) => {
        const rules = MODE_RULES[prev.mode];
        const maxImages = rules.maxImages;

        const merged = [...prev.images];
        images.forEach((img) => {
          if (!merged.some((x) => x.id === img.id)) {
            merged.push(img);
          }
        });

        const limited = merged.slice(0, maxImages);

        return {
          ...prev,
          images: limited,
          startImageId: limited[0]?.id,
          endImageId: limited[limited.length - 1]?.id,
        };
      });
    },
    [setStyle]
  );

  const onRemoveImage = useCallback(
    (id: string) => {
      setStyle((prev) => {
        const next = prev.images.filter((x) => x.id !== id);
        return {
          ...prev,
          images: next,
          startImageId: next[0]?.id,
          endImageId: next[next.length - 1]?.id,
        };
      });
    },
    [setStyle]
  );

  const onReorderImages = useCallback(
    (newOrder: ImageRef[]) => {
      // assume newOrder contains same ids
      setStyle((prev) => ({
        ...prev,
        images: newOrder,
        startImageId: newOrder[0]?.id,
        endImageId: newOrder[newOrder.length - 1]?.id,
      }));
    },
    [setStyle]
  );

  const onStartImageChange = useCallback(
    (id: string) => {
      setStyle((prev) => {
        if (!prev.images.some((x) => x.id === id)) return prev;
        return { ...prev, startImageId: id };
      });
    },
    [setStyle]
  );

  const onEndImageChange = useCallback(
    (id: string) => {
      setStyle((prev) => {
        if (!prev.images.some((x) => x.id === id)) return prev;
        return { ...prev, endImageId: id };
      });
    },
    [setStyle]
  );

  const setSlotImage = useCallback(
    (slot: 'start' | 'end', image: ImageRef) => {
      setStyle((prev) => {
        const rules = MODE_RULES[prev.mode];

        let images: ImageRef[];

        if (rules.maxImages === 1) {
          // Single-image modes always use the latest image.
          images = [image];
        } else {
          const existingIndex = prev.images.findIndex((x) => x.id === image.id);
          images = [...prev.images];

          if (existingIndex >= 0) {
            images[existingIndex] = image;
          } else {
            images.push(image);
          }

          if (images.length > rules.maxImages) {
            images = images.slice(images.length - rules.maxImages);
          }
        }

        let startImageId = prev.startImageId;
        let endImageId = prev.endImageId;

        if (slot === 'start') {
          startImageId = image.id;
        } else {
          endImageId = image.id;
        }

        let ordered = images;

        if (startImageId) {
          const idx = ordered.findIndex((img) => img.id === startImageId);
          if (idx > 0) {
            const copy = [...ordered];
            const [startImg] = copy.splice(idx, 1);
            copy.unshift(startImg);
            ordered = copy;
          }
        }

        if (endImageId && endImageId !== startImageId) {
          const idx = ordered.findIndex((img) => img.id === endImageId);
          if (idx >= 0 && idx !== ordered.length - 1) {
            const copy = [...ordered];
            const [endImg] = copy.splice(idx, 1);
            copy.push(endImg);
            ordered = copy;
          }
        }

        return {
          ...prev,
          images: ordered,
          startImageId,
          endImageId,
        };
      });
    },
    [setStyle]
  );

  // ---------- Adjustments actions ----------
  const updateAdjustments = useCallback(
    (updater: (prev: typeof adjustments) => typeof adjustments) => {
      setAdjustments((prev) => updater(prev));
    },
    [setAdjustments]
  );

  // ---------- Runtime actions ----------
  const setVideoName = useCallback(
    (name: string) => {
      setRuntime((prev) => ({ ...prev, videoName: name }));
    },
    [setRuntime]
  );

  const resetRuntime = useCallback(() => {
    setRuntime((prev) => ({
      ...prev,
      status: 'idle',
      previewUrl: undefined,
      errorMessage: undefined,
      taskId: undefined,
      lastCompletedTaskId: prev.lastCompletedTaskId,
    }));
  }, [setRuntime]);

  const onRetry = useCallback(() => {
    resetRuntime();
  }, [resetRuntime]);

  // ---------- Generate flow ----------
  const onGenerateClick = useCallback(async () => {
    if (!canGenerate) return;
    if (runtime.status === 'generating') return;

    setRuntime((prev) => ({
      ...prev,
      status: 'generating',
      previewUrl: undefined,
      errorMessage: undefined,
      taskId: undefined,
      lastCompletedTaskId: prev.lastCompletedTaskId,
    }));

    posthog.capture('image-to-video-created', {
      estateId: style.estateId,
      projectId: style.projectId,
      mode: style.mode,
      quality: style.quality,
      aspectRatio: style.aspectRatio,
      duration: style.durationSec,
      music: adjustments.music.enabled,
      speakerView: adjustments.speakerView.enabled,
      subtitles: adjustments.subtitles.enabled,
    });

    try {
      const res = await generateVideoMutation.mutateAsync({ style, adjustments });

      setRuntime((prev) => ({
        ...prev,
        status: 'generating',
        previewUrl: undefined,
        errorMessage: undefined,
        taskId: res.jobId,
        lastCompletedTaskId: prev.lastCompletedTaskId,
      }));
      toast.success(t('image2video-successfully-started'));

      // Scroll to the top of the page after success so the user can see the video progress
      requestAnimationFrame(() => {
        const scrollContainer = document.querySelector('[data-scroll-container]');
        scrollContainer?.scrollTo({ top: 0, behavior: 'smooth' });
      });
    } catch (e: unknown) {
      // biome-ignore lint/suspicious/noConsole: ignored
      console.error('Failed to start image-to-video job', e);

      const fallbackMessage = i18n.t('image-to-video-error');
      const message =
        e &&
        typeof e === 'object' &&
        'message' in e &&
        typeof (e as { message?: unknown }).message === 'string'
          ? (e as { message: string }).message
          : fallbackMessage;

      setRuntime(() => ({ status: 'error', errorMessage: message }));
      toast.error(message);
    }
  }, [adjustments, canGenerate, generateVideoMutation, runtime.status, setRuntime, style, posthog]);

  return {
    style,
    runtime,
    ui,
    adjustments,
    canGenerate,

    actions: {
      setTab,
      onEstateChange,
      onProjectChange,
      onModeChange,
      onAspectRatioChange,
      onCropToAspectRatioChange,
      onQualityChange,
      onDurationChange,

      onAddImages,
      onRemoveImage,
      onReorderImages,
      onStartImageChange,
      onEndImageChange,
      setSlotImage,

      updateAdjustments,
      onGenerateClick,
      onRetry,
      resetRuntime,
      setVideoName,
    },

    generateVideoMutation,
  };
};

export type ImageToVideoModel = ReturnType<typeof useImageToVideoActions>;
